<script setup>
import { computed, onMounted, nextTick, watch } from 'vue';
import { useSidebarContext } from './provider';
import { useRoute, useRouter } from 'vue-router';
import Policy from 'dashboard/components/policy.vue';
import SidebarGroupHeader from './SidebarGroupHeader.vue';
import SidebarGroupLeaf from './SidebarGroupLeaf.vue';
import SidebarSubGroup from './SidebarSubGroup.vue';
import SidebarGroupEmptyLeaf from './SidebarGroupEmptyLeaf.vue';

const props = defineProps({
  name: { type: String, required: true },
  label: { type: String, required: true },
  icon: { type: [String, Object, Function], default: null },
  to: { type: Object, default: null },
  activeOn: { type: Array, default: () => [] },
  children: { type: Array, default: undefined },
  getterKeys: { type: Object, default: () => ({}) },
  headerCount: { type: Number, default: 0 },
  // Direct count for non-expandable items (like VIP, Unattended)
  count: { type: Number, default: 0 },
});

const {
  setExpandedItem,
  isItemExpanded,
  resolvePath,
  resolvePermissions,
  resolveFeatureFlag,
  isAllowed,
} = useSidebarContext();

const navigableChildren = computed(() => {
  return props.children?.flatMap(child => child.children || child) || [];
});

const route = useRoute();
const router = useRouter();
const isExpanded = computed(() => isItemExpanded(props.name));
const isExpandable = computed(() => props.children);
const hasChildren = computed(
  () => Array.isArray(props.children) && props.children.length > 0
);

const hasAccessibleDescendant = item => {
  if (!item) return false;
  if (item.to) return isAllowed(item.to);
  if (Array.isArray(item.children)) {
    return item.children.some(child => hasAccessibleDescendant(child));
  }
  return false;
};

const findFirstAccessibleRoute = item => {
  if (!item) return null;
  if (item.to && isAllowed(item.to)) {
    return item;
  }
  if (Array.isArray(item.children)) {
    for (const child of item.children) {
      const accessibleChild = findFirstAccessibleRoute(child);
      if (accessibleChild) {
        return accessibleChild;
      }
    }
  }
  return null;
};

const accessibleItems = computed(() => {
  if (!hasChildren.value) return [];
  return props.children.filter(child => hasAccessibleDescendant(child));
});

const hasAccessibleChildren = computed(() => {
  return accessibleItems.value.length > 0;
});

const isActive = computed(() => {
  if (props.to) {
    if (route.path === resolvePath(props.to)) return true;

    return props.activeOn.includes(route.name);
  }

  return false;
});

// We could use the RouterLink isActive too, but our routes are not always
// nested correctly, so we need to check the active state ourselves
// TODO: Audit the routes and fix the nesting and remove this
const activeChild = computed(() => {
  const pathSame = navigableChildren.value.find(child => {
    if (!child.to) return false;
    const pathMatches = route.path === resolvePath(child.to);
    if (!pathMatches) return false;

    // 如果child.to有query参数，也需要匹配query
    if (child.to.query) {
      const queryMatches = Object.keys(child.to.query).every(
        key => route.query[key] === child.to.query[key]
      );
      return queryMatches;
    }

    return true;
  });
  if (pathSame) return pathSame;

  // Rank the activeOn Prop higher than the path match
  // There will be cases where the path name is the same but the params are different
  // So we need to rank them based on the params
  // For example, contacts segment list in the sidebar effectively has the same name
  // But the params are different
  const activeOnPages = navigableChildren.value.filter(child =>
    child.activeOn?.includes(route.name)
  );

  if (activeOnPages.length > 0) {
    // 优先匹配 query 参数（用于 All/Unassigned/Mine/Unread 分组）
    const queryMatchedPage = activeOnPages.find(child => {
      if (child.to.query) {
        return Object.keys(child.to.query).every(
          key => route.query[key] === child.to.query[key]
        );
      }
      return false;
    });
    if (queryMatchedPage) return queryMatchedPage;

    // 然后匹配 params
    const rankedPage = activeOnPages.find(child => {
      if (!child.to.params || Object.keys(child.to.params).length === 0) {
        return false;
      }
      return Object.keys(child.to.params)
        .map(key => {
          return String(child.to.params[key]) === String(route.params[key]);
        })
        .every(match => match);
    });

    // If there is no ranked page, return the first activeOn page anyway
    // Since this takes higher precedence over the path match
    // This is not perfect, ideally we should rank each route based on all the techniques
    // and then return the highest ranked one
    // But this is good enough for now
    return rankedPage ?? activeOnPages[0];
  }

  return navigableChildren.value.find(child => {
    if (!child.to) return false;
    const pathMatches = route.path.startsWith(resolvePath(child.to));
    if (!pathMatches) return false;

    // 如果child.to有query参数，也需要匹配query
    if (child.to.query) {
      const queryMatches = Object.keys(child.to.query).every(
        key => route.query[key] === child.to.query[key]
      );
      return queryMatches;
    }

    return true;
  });
});

const hasActiveChild = computed(() => {
  return activeChild.value !== undefined;
});

const syncExpandedStateForActiveChild = () => {
  if (hasActiveChild.value && !isExpanded.value) {
    setExpandedItem(props.name);
  }
};

const toggleTrigger = () => {
  if (
    hasAccessibleChildren.value &&
    !isExpanded.value &&
    !hasActiveChild.value
  ) {
    // if not already expanded, navigate to the first child
    const firstItem = accessibleItems.value
      .map(item => findFirstAccessibleRoute(item))
      .find(Boolean);

    if (firstItem?.to) {
      router.push(firstItem.to);
    }
  }
  setExpandedItem(props.name);
};

onMounted(async () => {
  await nextTick();
  syncExpandedStateForActiveChild();
});

watch(hasActiveChild, isActiveChild => {
  if (isActiveChild && !isExpanded.value) {
    setExpandedItem(props.name);
  }
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    v-if="!hasChildren || hasAccessibleChildren"
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="wlab-sidebar-group grid gap-1 text-sm cursor-pointer select-none"
  >
    <SidebarGroupHeader
      :icon
      :name
      :label
      :to
      :getter-keys="getterKeys"
      :header-count="headerCount"
      :count="count"
      :is-active="isActive"
      :has-active-child="hasActiveChild"
      :expandable="hasChildren"
      :is-expanded="isExpanded"
      @toggle="toggleTrigger"
    />
    <ul
      v-if="hasChildren"
      v-show="isExpanded"
      class="grid m-0 list-none sidebar-group-children"
    >
      <template v-for="child in children" :key="child.name">
        <SidebarSubGroup
          v-if="child.children"
          :label="child.label"
          :icon="child.icon"
          :children="child.children"
          :is-expanded="isExpanded"
          :active-child="activeChild"
        />
        <SidebarGroupLeaf
          v-else-if="isAllowed(child.to)"
          v-show="isExpanded"
          v-bind="child"
          :active="activeChild?.name === child.name"
        />
      </template>
    </ul>
    <ul v-else-if="isExpandable && isExpanded">
      <SidebarGroupEmptyLeaf />
    </ul>
  </Policy>
</template>

<style>
.wlab-sidebar--conversation .sidebar-group-children .child-item::before {
  content: '';
  position: absolute;
  width: 1px;
  /* 0.5px */
  height: 100%;
  opacity: 0.9;
  border-radius: 999px;
}

.wlab-sidebar--conversation
  .sidebar-group-children
  .child-item:first-child::before {
  border-radius: 4px 4px 0 0;
}

/* This selects the last child in a group */
/* https://codepen.io/scmmishra/pen/yLmKNLW */
.wlab-sidebar--conversation
  .sidebar-group-children
  > .child-item:last-child::before,
.wlab-sidebar--conversation
  .sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::before {
  height: 20%;
}

.wlab-sidebar--conversation
  .sidebar-group-children
  > .child-item:last-child::after,
.wlab-sidebar--conversation
  .sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  bottom: calc(50% - 2px);
  border-bottom-width: 1px;
  border-left-width: 1px;
  border-right-width: 0px;
  border-top-width: 0px;
  border-radius: 0 0 0 4px;
  left: 0;
  opacity: 0.9;
}

#app[dir='rtl']
  .wlab-sidebar--conversation
  .sidebar-group-children
  > .child-item:last-child::after,
#app[dir='rtl']
  .wlab-sidebar--conversation
  .sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::after {
  right: 0;
  border-bottom-width: 1px;
  border-right-width: 1px;
  border-left-width: 0px;
  border-top-width: 0px;
  border-radius: 0 0 4px 0px;
}
</style>
